import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { CreditRisk } from '@types';
import { RiskReport } from '@pages/credit-risk/export';

interface PdfPortalProps {
  risk: CreditRisk;
  onPdfGenerated: (blob: Blob) => void;
  isActive: boolean;
  hasGenerated?: boolean;
}

export const PdfPortal = ({
  risk,
  onPdfGenerated,
  isActive,
  hasGenerated,
}: PdfPortalProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.width = '800px';
    container.style.visibility = 'visible';

    document.body.appendChild(container);
    containerRef.current = container;
    setIsReady(true);

    return () => {
      if (container && document.body.contains(container)) {
        document.body.removeChild(container);
      }
    };
  }, [isActive]);

  useEffect(() => {
    if (!isReady || !risk || !containerRef.current || hasGenerated) return;

    const generatePdf = async () => {
      await new Promise(resolve => setTimeout(resolve, 500)); // Ждем рендеринга

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const canvas = await html2canvas(containerRef.current!.firstElementChild as HTMLElement);

      const imgWidth = 208;
      const pageHeight = 315;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      heightLeft -= pageHeight;

      const doc = new jsPDF('p', 'mm');
      doc.addImage(canvas, 'PNG', 0, position, imgWidth, imgHeight, '', 'FAST');

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(canvas, 'PNG', 0, position, imgWidth, imgHeight, '', 'FAST');
        heightLeft -= pageHeight;
      }

      const blob = doc.output('blob');
      onPdfGenerated(blob);
    };

    generatePdf();
  }, [isReady, risk, hasGenerated]);

  if (!isReady || !containerRef.current) {
    return null;
  }

  return createPortal(
    <RiskReport
      risk={risk}
      isExporting={true}
    />,
    containerRef.current,
  );
};
